import { useEffect, useRef, useState } from "react";
import * as AlertDialog from "@radix-ui/react-alert-dialog";
import { ChevronDown, ChevronLeft, ChevronRight, ClipboardCheck, Check, Wand2, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import {
  ActionButton,
  CustomerInput,
  CustomerPanel,
  StatusBadge,
} from "./customer/CustomerUI";

const SEV = {
  critical: { border: "border-l-health-critical", text: "text-health-critical", label: "Critical", bg: "bg-red-50" },
  warning: { border: "border-l-health-warn", text: "text-health-warn", label: "Warning", bg: "bg-amber-50" },
  info: { border: "border-l-slate-400", text: "text-slate-500", label: "Info", bg: "bg-slate-50" },
};

const recordKey = (record) =>
  `${record.contact_id}::${record.field || ""}::${record.suggested ?? record.new_value ?? ""}`;
const PAGE_SIZE = 50;

export default function ActionCard({
  finding,
  index,
  connectionId,
  scanId,
  onApplied,
  bulkDeleteCapability = { available: false, reason: null },
}) {
  const [open, setOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [appliedKeys, setAppliedKeys] = useState(new Set());
  const [deletedKeys, setDeletedKeys] = useState(new Set());
  const [deletePreview, setDeletePreview] = useState(null);
  const [confirmationInput, setConfirmationInput] = useState("");
  const [deleting, setDeleting] = useState(false);
  const deleteButtonRef = useRef(null);
  const toggleButtonRef = useRef(null);
  const [records, setRecords] = useState(finding.records || []);
  const [pageOffset, setPageOffset] = useState(0);
  const [pageMeta, setPageMeta] = useState({
    total_records: finding.records?.length || 0,
    affected_count: finding.affected_count || 0,
    has_more: (finding.affected_count || 0) > (finding.records?.length || 0),
    legacy_limited: false,
  });
  const [loadingRecords, setLoadingRecords] = useState(false);
  const sev = SEV[finding.severity] || SEV.info;
  const isConsent = finding.key === "consent_dnd";

  const copyReference = async (reference) => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(reference);
      toast.success("Record reference copied.");
    } catch {
      toast.error("Could not copy the reference. Select and copy it manually.");
    }
  };

  const applicableRecords = records.filter((r) => r.applicable);
  const hasApprovals = (finding.applicable_count ?? (finding.records || []).filter((r) => r.applicable).length) > 0;
  const canApprove = applicableRecords.length > 0;
  const canBulkDelete = Boolean(bulkDeleteCapability?.available);
  const selectableRecords = canBulkDelete ? records : applicableRecords;
  const canSelect = selectableRecords.length > 0;
  // Safe deterministic fixes start selected. Manual-review rows require an
  // explicit customer selection before the destructive confirmation flow.
  const [selected, setSelected] = useState(() => new Set(applicableRecords.map(recordKey)));

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoadingRecords(true);
    setSelected(new Set());
    api.get(`/scans/${scanId}/findings/${encodeURIComponent(finding.key)}/records`, {
      params: { offset: pageOffset, limit: PAGE_SIZE },
    }).then(({ data }) => {
      if (!active) return;
      const nextRecords = data.records || [];
      setRecords(nextRecords);
      setPageMeta(data);
      setSelected(new Set(nextRecords.filter((record) => record.applicable).map(recordKey)));
    }).catch((error) => {
      if (!active) return;
      if (pageOffset === 0) {
        const fallback = finding.records || [];
        setRecords(fallback);
        setSelected(new Set(fallback.filter((record) => record.applicable).map(recordKey)));
      } else {
        setPageOffset(0);
      }
      toast.error(formatApiError(error.response?.data?.detail || "Finding records could not be loaded."));
    }).finally(() => {
      if (active) setLoadingRecords(false);
    });
    return () => { active = false; };
  }, [open, pageOffset, scanId, finding.key, finding.records]);

  const toggle = (key) => {
    const next = new Set(selected);
    next.has(key) ? next.delete(key) : next.add(key);
    setSelected(next);
  };

  const remainingSelectable = selectableRecords.filter((r) => !deletedKeys.has(r.contact_id));
  const selectAll = () => setSelected(new Set(remainingSelectable.map(recordKey)));
  const clearSelection = () => setSelected(new Set());

  const previewDelete = async () => {
    const contactIds = [...new Set(remainingSelectable
      .filter((r) => selected.has(recordKey(r)))
      .map((r) => r.contact_id))];
    if (contactIds.length === 0) { toast.error("Select at least one contact to delete."); return; }
    try {
      const { data } = await api.post(`/connections/${connectionId}/bulk-delete/preview`, {
        scan_id: scanId, finding_key: finding.key, contact_ids: contactIds,
      });
      setConfirmationInput("");
      setDeletePreview(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const confirmDelete = async () => {
    if (!deletePreview) return;
    setDeleting(true);
    try {
      const contactIds = deletePreview.contacts.map((contact) => contact.contact_id);
      const { data } = await api.post(`/connections/${connectionId}/bulk-delete`, {
        scan_id: scanId, finding_key: finding.key, contact_ids: contactIds,
        confirmation: confirmationInput,
        confirmation_token: deletePreview.confirmation_token,
      });
      setDeletedKeys(new Set([...deletedKeys, ...(data.deleted_ids || [])]));
      setSelected(new Set());
      setDeletePreview(null);
      setConfirmationInput("");
      toast.success(`Deleted ${data.deleted} contact${data.deleted === 1 ? "" : "s"}${data.failed ? `; ${data.failed} failed` : ""}. Run a fresh scan.`);
      onApplied && onApplied();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const closeDeletePreview = () => {
    setDeletePreview(null);
    setConfirmationInput("");
  };

  const apply = async () => {
    const changes = applicableRecords
      .filter((r) => selected.has(recordKey(r)) && !appliedKeys.has(recordKey(r)))
      .map((r) => ({ contact_id: r.contact_id, contact_name: r.name, field: r.field,
                     current: r.current, new_value: r.suggested, finding_key: finding.key }));
    if (changes.length === 0) { toast.error("Select at least one record to approve."); return; }
    setApplying(true);
    try {
      const { data } = await api.post(`/connections/${connectionId}/apply-changes`, { scan_id: scanId, changes });
      toast.success(`Applied ${data.applied} approved fix${data.applied === 1 ? "" : "es"}${data.failed ? `, ${data.failed} failed` : ""}`);
      const failed = new Set((data.results || [])
        .filter((result) => result.status === "failed")
        .map(recordKey));
      const completed = changes.map(recordKey).filter((key) => !failed.has(key));
      setAppliedKeys(new Set([...appliedKeys, ...completed]));
      setSelected(new Set([...selected].filter((key) => !completed.includes(key))));
      onApplied && onApplied();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setApplying(false); }
  };

  return (
    <CustomerPanel data-testid={`action-card-${finding.key}`}
         className={`animate-fade-up border-l-4 ${sev.border}`}
         style={{ animationDelay: `${index * 45}ms` }}>
      <button ref={toggleButtonRef} data-testid={`action-card-toggle-${finding.key}`} onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              className="flex min-h-touch w-full items-start justify-between gap-4 rounded-customer p-4 text-left transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`font-mono text-[10px] uppercase tracking-[0.15em] ${sev.text}`}>{sev.label}</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">/ {finding.category}</span>
            {hasApprovals ? (
              <StatusBadge data-testid={`action-card-mode-${finding.key}`} tone="success">
                <Wand2 className="h-3 w-3" /> One-click correction · approval required
              </StatusBadge>
            ) : (
              <StatusBadge data-testid={`action-card-mode-${finding.key}`}>
                <ClipboardCheck className="h-3 w-3" /> Manual review required
              </StatusBadge>
            )}
            {canBulkDelete && (
              <StatusBadge tone="danger">
                <Trash2 className="h-3 w-3" /> Bulk action available
              </StatusBadge>
            )}
          </div>
          <h3 className="mt-1 text-base font-semibold text-slate-900">{finding.title}</h3>
          <p className="mt-0.5 text-sm text-slate-500">{finding.description}</p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <div className="text-right">
            <div className="font-mono text-2xl font-bold tracking-tight text-slate-900" data-testid={`action-card-count-${finding.key}`}>{finding.affected_count}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">records</div>
          </div>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {open && (
        <div className="border-t border-border">
          <div className={`px-4 py-3 text-sm text-slate-700 ${sev.bg}`}>
            <span className="font-medium">Recommendation: </span>{finding.recommendation}
          </div>
          {canSelect && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-white px-4 py-2">
              <span className="text-xs text-slate-500">
                {selected.size} of {remainingSelectable.length} records on this page selected
              </span>
              <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
                <ActionButton type="button" data-testid={`action-card-select-all-${finding.key}`}
                        onClick={selectAll} disabled={loadingRecords}
                        variant="secondary" size="compact">
                  Select page
                </ActionButton>
                <ActionButton type="button" data-testid={`action-card-clear-${finding.key}`}
                        onClick={clearSelection} disabled={selected.size === 0 || loadingRecords}
                        variant="quiet" size="compact">
                  Clear selection
                </ActionButton>
                {canBulkDelete && (
                  <ActionButton ref={deleteButtonRef} type="button" data-testid={`action-card-delete-${finding.key}`}
                          onClick={previewDelete} disabled={selected.size === 0 || deleting || loadingRecords}
                          variant="destructive" size="compact" className="bg-red-700 text-white hover:bg-red-800">
                    <Trash2 className="h-3.5 w-3.5" /> Delete selected
                  </ActionButton>
                )}
                {canApprove && (
                  <ActionButton type="button" data-testid={`action-card-apply-top-${finding.key}`}
                          onClick={apply} disabled={selected.size === 0 || applying || loadingRecords}
                          size="compact">
                    {applying ? "Applying…" : "Apply selected"}
                  </ActionButton>
                )}
              </div>
            </div>
          )}
          <div className="max-h-72 overflow-auto">
            {loadingRecords && (
              <div className="border-b border-border bg-slate-50 px-4 py-3 text-xs text-slate-500">
                Loading finding records…
              </div>
            )}
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-100">
                <tr className="text-left font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">
                  {canSelect && <th className="px-4 py-2 font-medium">Select</th>}
                  <th className="px-4 py-2 font-medium">Contact</th>
                  <th className="px-4 py-2 font-medium">Issue</th>
                  <th className="px-4 py-2 font-medium">Current</th>
                  <th className="px-4 py-2 font-medium">{isConsent ? "Suggested" : "Suggested / next step"}</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r, i) => {
                  const key = recordKey(r);
                  const done = appliedKeys.has(key) || deletedKeys.has(r.contact_id);
                  const showReference = !isConsent && !r.applicable &&
                    typeof r.contact_id === "string" && r.contact_id.trim().length > 0;
                  return (
                    <tr key={i} className="border-t border-border">
                      {canSelect && (
                        <td className="px-4 py-2">
                          {done ? <Check className="h-4 w-4 text-health-good" /> : (
                            <label className="flex min-h-touch min-w-touch cursor-pointer items-center justify-center">
                              <input type="checkbox" data-testid={`approve-checkbox-${finding.key}-${r.contact_id}`}
                                     checked={selected.has(key)} onChange={() => toggle(key)}
                                     disabled={loadingRecords}
                                     className="h-4 w-4 accent-slate-900" />
                              <span className="sr-only">Select {r.name || "record"}</span>
                            </label>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-2 font-medium text-slate-900">
                        <div className="max-w-48 break-words [overflow-wrap:anywhere]">{r.name}</div>
                        {showReference && (
                          <div className="mt-2 max-w-48 text-xs font-normal text-slate-600">
                            <div>Record reference</div>
                            <div className="break-words font-mono [overflow-wrap:anywhere]">{r.contact_id}</div>
                            <ActionButton type="button" variant="quiet" size="compact"
                              aria-label={`Copy reference for ${r.name || "record"}`}
                              onClick={() => copyReference(r.contact_id)}>
                              Copy reference
                            </ActionButton>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-slate-600">{r.issue}</td>
                      <td className="px-4 py-2 font-mono text-xs text-slate-500">{r.current || "—"}</td>
                      <td className="px-4 py-2 text-xs text-slate-700">
                        {isConsent ? <span className="font-mono">{r.suggested || "—"}</span> : (
                          <>
                            {r.suggested != null && r.suggested !== "" && (
                              <div className="font-mono">{r.suggested}</div>
                            )}
                            <div>{deletedKeys.has(r.contact_id) ? "Contact deleted" : appliedKeys.has(key)
                              ? "Correction applied" : r.applicable
                                ? "CleanIQ can apply · approval required" : "Review in your CRM"}</div>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pageMeta.legacy_limited && (
            <div className="border-t border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
              This historical scan saved only its first 50 details. Run a fresh scan to review every affected record.
            </div>
          )}
          {(pageOffset > 0 || pageMeta.has_more) && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-slate-50 px-4 py-2">
              <span className="text-xs text-slate-500" data-testid={`action-card-page-status-${finding.key}`}>
                Showing {pageOffset + 1}–{pageOffset + records.length} of {pageMeta.total_records} available records
              </span>
              <div className="flex items-center gap-2">
                <ActionButton type="button" onClick={() => setPageOffset(Math.max(0, pageOffset - PAGE_SIZE))}
                        disabled={pageOffset === 0 || loadingRecords}
                        data-testid={`action-card-page-previous-${finding.key}`}
                        variant="secondary" size="compact">
                  <ChevronLeft className="h-3.5 w-3.5" /> Previous
                </ActionButton>
                <ActionButton type="button" onClick={() => setPageOffset(pageOffset + PAGE_SIZE)}
                        disabled={!pageMeta.has_more || loadingRecords}
                        data-testid={`action-card-page-next-${finding.key}`}
                        variant="secondary" size="compact">
                  Next <ChevronRight className="h-3.5 w-3.5" />
                </ActionButton>
              </div>
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-white p-3">
            {canApprove ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                <Check className="h-3.5 w-3.5 text-health-good" /> {selected.size} of {applicableRecords.length} selected · writes only what you approve
              </span>
            ) : canBulkDelete ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                <AlertTriangle className="h-3.5 w-3.5 text-red-700" /> Manual correction required · bulk deletion always requires confirmation
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
                <ClipboardCheck className="h-3.5 w-3.5" /> Manual review required · {bulkDeleteCapability?.reason || "Bulk deletion is unavailable."}
              </span>
            )}
            {canApprove && (
              <ActionButton data-testid={`action-card-apply-${finding.key}`}
                      disabled={selected.size === 0 || applying || loadingRecords}
                      onClick={apply}
                      size="compact">
                {applying ? "Applying…" : "Apply approved fixes"}
              </ActionButton>
            )}
          </div>
        </div>
      )}

      {deletePreview && (
        <AlertDialog.Root open onOpenChange={(nextOpen) => { if (!nextOpen && !deleting) closeDeletePreview(); }}>
        <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 z-50 bg-slate-950/60" />
        <AlertDialog.Content
          className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto"
          onEscapeKeyDown={(event) => { if (deleting) event.preventDefault(); }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const trigger = deleteButtonRef.current;
            (trigger && !trigger.disabled ? trigger : toggleButtonRef.current)?.focus();
          }}>
          <CustomerPanel className="w-full max-w-lg border-red-300 p-ui-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-700" />
              <div>
                <AlertDialog.Title className="font-heading text-lg font-bold text-slate-900">
                  Delete {deletePreview.count} contact{deletePreview.count === 1 ? "" : "s"}?
                </AlertDialog.Title>
                <AlertDialog.Description className="mt-1 text-sm text-slate-600">
                  This permanently deletes these contacts from {deletePreview.connector_name}. CleanIQ cannot undo this operation.
                </AlertDialog.Description>
              </div>
            </div>
            <div className="mt-4 max-h-40 overflow-auto border border-border">
              {deletePreview.contacts.map((contact) => (
                <div key={contact.contact_id} className="border-b border-border px-3 py-2 text-sm last:border-b-0">
                  <span className="font-medium text-slate-900">{contact.name}</span>
                  <span className="ml-2 text-xs text-slate-500">{contact.issue}</span>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <label
                htmlFor={`bulk-delete-confirmation-${finding.key}`}
                className="mb-2 block text-sm font-medium text-slate-800"
              >
                Type {deletePreview.confirmation} to confirm
              </label>
              <CustomerInput
                id={`bulk-delete-confirmation-${finding.key}`}
                value={confirmationInput}
                onChange={(event) => setConfirmationInput(event.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <AlertDialog.Cancel asChild>
              <ActionButton type="button" disabled={deleting} variant="secondary">
                Cancel
              </ActionButton>
              </AlertDialog.Cancel>
              <ActionButton type="button" data-testid="bulk-delete-confirm"
                      onClick={confirmDelete}
                      disabled={deleting || confirmationInput !== deletePreview.confirmation}
                      variant="destructive" className="bg-red-700 text-white hover:bg-red-800">
                {deleting ? "Deleting…" : "Confirm deletion"}
              </ActionButton>
            </div>
          </CustomerPanel>
        </AlertDialog.Content>
        </AlertDialog.Portal>
        </AlertDialog.Root>
      )}
    </CustomerPanel>
  );
}
